import type { RefObject } from "react";
import { OspToast } from "@ospweb/sdk-ui";
import log from "logbeacon";
import type { Bridge } from "./bridge";

export type PrepareFileInputMode = "library" | "document" | "camera";

export type PrepareFileInputParams = {
  mode: PrepareFileInputMode;
  accept: string;
  capture: string | null;
  multiple: boolean;
};

export type FilePickPresetId = "photo" | "camera" | "files";

/** Photo / Camera / Files 与隐藏 `<input type="file">` 一一对应 */
export const FILE_PICK_PRESETS: Record<
  FilePickPresetId,
  PrepareFileInputParams
> = {
  photo: {
    mode: "library",
    accept: "image/*",
    capture: null,
    multiple: true,
  },
  camera: {
    mode: "camera",
    accept: "image/*",
    capture: "environment",
    multiple: false,
  },
  files: {
    mode: "document",
    accept: "*/*",
    capture: null,
    multiple: true,
  },
};

const PREPARE_FILE_INPUT_METHOD = "prepareFileInput";
const PREPARE_FILE_INPUT_ERROR_MESSAGE = "Failed to open file picker";

type WebKitBridgeHandler = {
  postMessage: (message: unknown) => void;
};

function getWebKitBridgeHandler(): WebKitBridgeHandler | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  return (
    window as Window & {
      webkit?: { messageHandlers?: { bridge?: WebKitBridgeHandler } };
    }
  ).webkit?.messageHandlers?.bridge;
}

export function shouldPrepareNativeFileInput(bridge: Bridge): boolean {
  return bridge.isInApp() && bridge.getPlatform() === "ios";
}

function assertCameraInputHasCapture(input: HTMLInputElement): void {
  if (process.env.NODE_ENV === "production") {
    return;
  }
  const capture = input.capture || input.getAttribute("capture")?.trim() || "";
  if (!capture) {
    log.warn(
      "[bridge][prepareFileInput] camera preset requires input with capture attribute",
    );
  }
}

export async function prepareNativeFileInput(
  bridge: Bridge,
  params: PrepareFileInputParams,
): Promise<void> {
  if (!shouldPrepareNativeFileInput(bridge)) {
    return;
  }
  const handler = getWebKitBridgeHandler();
  if (!handler) {
    throw new Error(
      "[bridge][prepareFileInput] iOS bridge handler not available",
    );
  }
  try {
    handler.postMessage({
      type: "call-method",
      data: {
        method: PREPARE_FILE_INPUT_METHOD,
        params: {
          mode: params.mode,
          accept: params.accept || "",
          capture: params.capture || "",
          multiple: params.multiple,
        },
      },
    });
  } catch (error) {
    log.warn("[bridge][prepareFileInput] postMessage failed", error);
    throw error;
  }
}

function showPrepareFileInputError(): void {
  OspToast.error({ content: PREPARE_FILE_INPUT_ERROR_MESSAGE });
}

/**
 * iOS App 壳：先 prepareFileInput，再 click；其它环境直接 click。
 * prepare 失败时不 click。
 */
export async function clickHiddenFileInput(
  bridge: Bridge,
  inputRef: RefObject<HTMLInputElement | null>,
  preset: PrepareFileInputParams,
): Promise<void> {
  const input = inputRef.current;
  if (!input) {
    return;
  }

  if (preset.mode === "camera") {
    assertCameraInputHasCapture(input);
  }

  if (shouldPrepareNativeFileInput(bridge)) {
    try {
      await prepareNativeFileInput(bridge, preset);
    } catch (error) {
      log.warn("[bridge][prepareFileInput] prepare failed", error);
      showPrepareFileInputError();
      return;
    }
  }

  input.click();
}
